import { SensorAlertSeverity, SensorType, useGetWaterQualityOverTime } from 'common';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';

import { HumidityAlertCard } from './components/HumidityAlertCard';
import { HumidityContent } from './components/HumidityContent';
import { LeakDetectedContent } from './components/LeakDetectedContent';
import { WaterAlertContent } from './components/WaterAlertContent';
import { WaterQualityContent } from './components/WaterQualityContent';
import { WaterQualityOverTimeContent } from './components/WaterQualityOverTimeContent';
import { WidgetsWrapper } from '../../components/common';
import {
  DraggableItemData,
  DraggableWidgetCard,
} from '../../components/common/DraggableWidgetCard';
import { DraggableWrapper } from '../../components/common/DraggableWrapper';
import { useAppSync } from '../../contexts/AppSyncContext';
import {
  HumidityAlertProvider,
  useHumidityAlertContext,
} from '../../contexts/HumidityAlertContext';
import {
  WaterAlertProvider,
  WaterAlertState,
  useWaterAlertContext,
} from '../../contexts/WaterAlertContext';
import { useWidgetOrder } from '../../hooks';
import { useGetWaterQualityData } from '../../hooks/water/useGetWaterQualityData';
import { useHumidity } from '../../hooks/water/useHumidity';

export enum WaterWidgetCardTypes {
  WaterAlert = 'WaterAlert',
  HumidityAlert = 'HumidityAlert',
  Humidity = 'Humidity',
  WaterQuality = 'WaterQuality',
  WaterQualityOverTime = 'WaterQualityOverTime',
  Leak = 'Leak',
}

const renderContent = (type: WaterWidgetCardTypes) => {
  switch (type) {
    case WaterWidgetCardTypes.WaterAlert:
      return <WaterAlertContent />;
    case WaterWidgetCardTypes.HumidityAlert:
      return <HumidityAlertCard />;
    case WaterWidgetCardTypes.Humidity:
      return <HumidityContent />;
    case WaterWidgetCardTypes.WaterQuality:
      return <WaterQualityContent />;
    case WaterWidgetCardTypes.WaterQualityOverTime:
      return <WaterQualityOverTimeContent />;
    case WaterWidgetCardTypes.Leak:
      return <LeakDetectedContent />;
  }
};

const WaterContent: React.FC = () => {
  const { t } = useTranslation();
  const waterQualityData = useGetWaterQualityData();
  const { sensors, selectedHubId } = useAppSync();
  const { alertState } = useWaterAlertContext();
  const { hasActiveHumidityAlerts, alertSeverity: humidityAlertSeverity } =
    useHumidityAlertContext();

  const leakSensor = useMemo(
    () =>
      sensors.find(
        (sensor) => sensor.hubId === selectedHubId && sensor.type === SensorType.LEAK_DETECTOR,
      ),
    [sensors, selectedHubId],
  );

  const waterSensor = useMemo(
    () =>
      sensors.find(
        (sensor) => sensor.hubId === selectedHubId && sensor.type === SensorType.WATER_QUALITY,
      ),
    [sensors, selectedHubId],
  );

  const { lastReported: humidityLastReported } = useHumidity(leakSensor?.id ?? '');
  const { lastReported: waterQualityLastReported } = useGetWaterQualityOverTime(
    waterSensor?.id ?? '',
  );

  // Get dynamic title for humidity alert based on severity
  const getHumidityAlertTitle = () => {
    if (!humidityAlertSeverity) {
      return t('humidity.alert');
    }

    switch (humidityAlertSeverity) {
      case SensorAlertSeverity.High:
        return t('water.highHumidityAlertTitle');
      case SensorAlertSeverity.Critical:
        return t('water.extremeHumidityAlertTitle');
      default:
        return t('water.cautionaryHumidityAlertTitle');
    }
  };

  const data: DraggableItemData<WaterWidgetCardTypes>[] = useMemo(
    () => [
      {
        id: '1',
        title: t('water.criticalAlert'),
        type: WaterWidgetCardTypes.WaterAlert,
      },
      {
        id: '2',
        title: getHumidityAlertTitle(),
        type: WaterWidgetCardTypes.HumidityAlert,
      },
      {
        id: '3',
        title: leakSensor?.location ?? t('water.leak'),
        type: WaterWidgetCardTypes.Leak,
        timestamp: humidityLastReported ?? undefined,
      },
      {
        id: '4',
        title: t('water.waterQuality'),
        type: WaterWidgetCardTypes.WaterQuality,
        timestamp: waterQualityData?.lastReported,
      },
      {
        id: '5',
        title: t('water.waterQualityOverTime'),
        type: WaterWidgetCardTypes.WaterQualityOverTime,
        timestamp: waterQualityData?.lastReported ?? undefined,
      },
      {
        id: '6',
        title: leakSensor?.location ?? t('water.humidity'),
        type: WaterWidgetCardTypes.Humidity,
        timestamp: humidityLastReported ?? undefined,
      },
    ],
    [
      t,
      alertState,
      humidityAlertSeverity,
      waterQualityData?.lastReported,
      leakSensor?.location,
      humidityLastReported,
      waterQualityLastReported,
    ],
  );

  const {
    items,
    handleItemsReordered,
    handleStartDragging,
    handleSaveOrder,
    handleCancelOrder,
    isItemEnabled,
    setItemEnabled,
  } = useWidgetOrder<DraggableItemData<WaterWidgetCardTypes>>('waterWidgetsOrder', data);

  const filteredItems = items.filter((item) => {
    if (
      !waterSensor &&
      (item.type === WaterWidgetCardTypes.WaterQuality ||
        item.type === WaterWidgetCardTypes.WaterQualityOverTime)
    ) {
      return false;
    }
    return true;
  });

  const renderItem = (item: DraggableItemData<WaterWidgetCardTypes>, index: number) => {
    const isNoAlertCard =
      item.type === WaterWidgetCardTypes.WaterAlert && alertState === WaterAlertState.NoAlert;
    const isNoHumidityAlertCard =
      item.type === WaterWidgetCardTypes.HumidityAlert && !hasActiveHumidityAlerts;

    if (isNoAlertCard || isNoHumidityAlertCard) {
      return null;
    }

    return (
      <DraggableWidgetCard
        key={item.id}
        item={item}
        timestamp={item.timestamp}
        index={index}
        enabled={isItemEnabled(item.id)}
        onEnabledChange={(enabled) => setItemEnabled(item.id, enabled)}>
        {item.type && renderContent(item.type)}
      </DraggableWidgetCard>
    );
  };

  return (
    <WidgetsWrapper
      onStartDragging={handleStartDragging}
      onSaveOrder={handleSaveOrder}
      onCancelOrder={handleCancelOrder}>
      <XStack flex={1}>
        <DraggableWrapper items={filteredItems} onItemsReordered={handleItemsReordered}>
          {filteredItems.map(renderItem)}
        </DraggableWrapper>
      </XStack>
    </WidgetsWrapper>
  );
};

export const Water: React.FC = () => (
  <WaterAlertProvider>
    <HumidityAlertProvider>
      <WaterContent />
    </HumidityAlertProvider>
  </WaterAlertProvider>
);
